<script>
import { validationMixin } from 'vuelidate'
import { required, minLength, sameAs } from 'vuelidate/lib/validators'
import CustomNotification from '@/components/common/CustomNotification.vue'
import accountManagementJson from '@/data/json/profile/accountManagement/accountManagement.json'
import errorsJson from '@/data/json/errors/errors.json'
import validationsJson from '@/data/json/errors/validations.json'
import { mapGetters } from 'vuex'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import CustomAlert from '@/components/common/CustomAlert.vue'

export default {
  name: 'ChangePassword',
  components: { CustomAlert, CustomNotification },
  mixins: [validationMixin, fieldValidationsMixin],
  validations: {
    currentPwd: { required },
    pwd: {
      required,
      minLength: minLength(6),
      hasCapital(pwd) {
        return /[A-Z]/.test(pwd)},
      hasNum(pwd) {
        return /[0-9]/.test(pwd)},
      hasSpecialChar(pwd) {
        return /\W|_/.test(pwd)},
      isNew(pwd) {
        return this.currentPwd !== pwd} },
    repeatPwd: { required, sameAsPassword: sameAs('pwd'), minLength: minLength(6) }
  },
  data: () => ({
    content: accountManagementJson.data.concat(errorsJson.data, validationsJson.data),
    errorContext: "",
    errorMessage: '',
    currentPwd: '',
    pwd: '',
    repeatPwd: '',
    submitError: false,
    showPwdNotification: false,
    passwordChangeStatus: { type: 'error', msg: '' },
    showCurrentPwd: false,
    showPwd: false,
    showRepeatPwd: false,
    wrongCurrentPwd: false}),
  computed: {
    ...mapGetters(['locale']),
    currentPwdErrors() {
      const errors = []
      if (!this.$v.currentPwd.$dirty) return errors
      !this.$v.currentPwd.required &&
      errors.push(this.$t('validation.password.required'))
      this.wrongCurrentPwd &&
      errors.push(this.$t('validation.current.password.mismatch'))
      return errors
    },
    pwdErrors() {
      const errors = []
      if (!this.$v.pwd.$dirty) return errors
      !this.$v.pwd.required &&
      errors.push(this.$t('validation.password.required'))
      !this.$v.pwd.minLength &&
      errors.push(this.$t('validation.password.min.length'))
      !this.$v.pwd.hasCapital &&
      errors.push(this.$t('validation.password.capital.letter.required'))
      !this.$v.pwd.hasNum &&
      errors.push(this.$t('validation.password.number.required'))
      !this.$v.pwd.hasSpecialChar &&
      errors.push(this.$t('validation.password.special.char.required'))
      !this.$v.pwd.isNew &&
      errors.push(this.$t('validation.password.not.new'))
      return errors
    },
    repeatPwdErrors() {
      const errors = []
      if (!this.$v.repeatPwd.$dirty) return errors
      !this.$v.repeatPwd.required &&
      errors.push(this.$t('validation.password.repeat.required'))
      !this.$v.repeatPwd.minLength &&
      errors.push(this.$t('validation.password.repeat.min.length'))
      !this.$v.repeatPwd.sameAsPassword &&
      errors.push(this.$t('validation.passwords.not.identical'))
      return errors
    }
  },
  watch: {
    '.$error': {
      handler(hasError) {
        if (!hasError && this.submitError) this.submitError = false},
      deep: true }
  },
  methods: {
    submitPasswordChange() {
      this.$v.currentPwd.$touch()
      this.$v.pwd.$touch()
      this.$v.repeatPwd.$touch()
      this.submitError = false
      this.errorMessage = ""
      this.errorContext = ""
      if (this.$v.currentPwd.$invalid || this.$v.pwd.$invalid || this.$v.repeatPwd.$invalid) {
        this.submitError = true
        this.errorMessage = this.$t('page.resetPassword.view.validator.message')
        this.errorContext = "password"
        return }
      this.$userService
        .changePassword({ currentPassword: this.currentPwd, newPassword: this.pwd, confirmPassword: this.repeatPwd})
        .then(() => {
          this.submitError = false
          this.showPwdNotification = true
          this.passwordChangeStatus = { type: "success",
            msg: this.$t('component.profile.accountManagement.change.password.success')} })
        .catch((e) => {
          this.submitError = true
          this.errorMessage =
            e.response?.status === 400
              ? this.$t('validation.password.mismatch')
              : e.fromCms ? e.message : this.$t(e.response?.status || 'page.resetPassword.view.validator.message')
          this.errorContext = "password"
        })
    }
  }
}
</script>
<template>
  <div>
    <CustomNotification
      :show-notification="showPwdNotification"
      :notification-type="passwordChangeStatus.type"
      :timeout= 5000>
      <span class="body-2">{{ passwordChangeStatus.msg }}</span>
    </CustomNotification>
    <v-card flat tile outlined color="transparent" class="pa-4">
      <v-card-text>
        <v-row align="center" justify="center">
          <v-col cols="12" md="6">
            <v-card outlined class="mb-4">
              <v-card-title>
                <v-spacer />
                <v-row class="pl-2">
                  <v-col cols="12" class="justify-center">
                    <span>{{ $t('component.profile.accountManagement.change.password.header') }}</span>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <CustomAlert
                  v-if="submitError && errorContext === 'password'"
                  alert-type="error"
                  :show-alert="submitError"
                  @update:show-alert="submitError = $event">
                  <span class="body-2 cms_validatorMsg">{{ errorMessage }}</span>
                </CustomAlert>
                <v-row>
                  <v-col>
                    <v-text-field
                      id="currentPwd"
                      v-model="currentPwd"
                      :label="$t('component.profile.accountManagement.current.password')"
                      :error-messages="currentPwdErrors"
                      :type="showCurrentPwd ? 'text' : 'password'"
                      :append-icon="showCurrentPwd ? 'mdi-eye' : 'mdi-eye-off'"
                      outlined
                      dense
                      required
                      @input="$v.currentPwd.$touch()"
                      @click:append="showCurrentPwd = !showCurrentPwd"
                      @keyup.enter.native="submitPasswordChange()"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      id="password"
                      v-model="pwd"
                      class="cms_pw"
                      :label="$t('component.profile.accountManagement.new.password')"
                      :error-messages="pwdErrors"
                      :type="showPwd ? 'text' : 'password'"
                      :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                      counter
                      outlined
                      dense
                      required
                      @input="$v.pwd.$touch()"
                      @click:append="showPwd = !showPwd"
                      @keyup.enter.native="submitPasswordChange()"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      id="repeatPassword"
                      v-model="repeatPwd"
                      class="cms_repeatPw"
                      :label="$t('component.profile.accountManagement.new.password')"
                      :error-messages="repeatPwdErrors"
                      :type="showRepeatPwd ? 'text' : 'password'"
                      :append-icon="showRepeatPwd ? 'mdi-eye' : 'mdi-eye-off'"
                      counter
                      outlined
                      dense
                      required
                      @input="$v.repeatPwd.$touch()"
                      @click:append="showRepeatPwd = !showRepeatPwd"
                      @keyup.enter.native="submitPasswordChange()"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-row class="text-center">
                  <v-col class="pt-0">
                    <v-btn
                      id="saveNewPassword"
                      class="text-center px-8"
                      color="primary"
                      depressed
                      @click.stop="submitPasswordChange">
                      {{ $t('button.change.password') }}</v-btn
                    ></v-col
                  ></v-row
                ></v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>